import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
  aiHubListDataGridRowBorderSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import type { AiHubStatusTone } from "../aiHubChipStyles";
import { buildAiHubObjectAnalysisPath } from "../constants";
import AiHubTableToolbar from "../AiHubTableToolbar";
import AssessmentStatusChip, {
  listAssessmentStatusHint,
} from "./AssessmentStatusChip";
import ObjectAnalysisRowActions from "./ObjectAnalysisRowActions";
import {
  formatAssessmentStatus,
  type ObjectAnalysisSummaryRow,
} from "./objectAnalysisData";

type ObjectAnalysisSummaryTableProps = Readonly<{
  rows: ObjectAnalysisSummaryRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}>;

const STATUS_TONES: Record<ObjectAnalysisSummaryRow["status"], AiHubStatusTone> =
  {
    completed: "green",
    failed: "red",
    partial: "yellow",
    inProgress: "blue",
  };

function NoRowsOverlay() {
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 280,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.paper",
      }}
    >
      <Box component="img" src={noDataAnim} alt="" sx={{ width: 160, mb: 2 }} />
      <Typography sx={{ color: "#8c8c8c", fontSize: 14 }}>
        No data available.
      </Typography>
    </Box>
  );
}

function ObjectCell({ type, detail }: { type: string; detail: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        height: "100%",
        fontSize: 13,
      }}
    >
      <Box component="span" sx={{ fontWeight: 600, color: "#262626" }}>
        {type}
      </Box>
      <Box component="span" sx={{ color: "#8c8c8c" }}>
        · {detail}
      </Box>
    </Box>
  );
}

export default function ObjectAnalysisSummaryTable({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
}: ObjectAnalysisSummaryTableProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const panelScrollTopRef = useRef(0);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={panelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const toolbarSlot = useCallback(
    () => <AiHubTableToolbar showExport={false} />,
    [],
  );

  const columns = useMemo<GridColDef<ObjectAnalysisSummaryRow>[]>(
    () => [
      {
        field: "assessmentId",
        headerName: "Result ID",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Link
              component="button"
              type="button"
              underline="none"
              sx={{ fontSize: 13 }}
              onClick={() =>
                navigate(buildAiHubObjectAnalysisPath(row.assessmentId))
              }
            >
              {row.assessmentId}
            </Link>
          </Box>
        ),
      },
      {
        field: "planId",
        headerName: "Assessment Plan",
        width: 190,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.planLabel,
      },
      {
        field: "objectA",
        headerName: "Object A",
        flex: 1,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <ObjectCell
            type={row.objectATypeLabel || "-"}
            detail={`${row.objectACount} ${row.objectAUnit}`}
          />
        ),
      },
      {
        field: "objectB",
        headerName: "Object B",
        flex: 1,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <ObjectCell
            type={row.objectBTypeLabel || "-"}
            detail={`${row.objectBCount} ${row.objectBUnit}`}
          />
        ),
      },
      {
        field: "assessmentTime",
        headerName: "Assessment Time",
        width: 170,
        sortable: false,
        filterable: false,
      },
      {
        field: "status",
        headerName: "Assessment Status",
        width: 175,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <AssessmentStatusChip
              label={formatAssessmentStatus(row)}
              tone={STATUS_TONES[row.status]}
              hint={listAssessmentStatusHint(row.status)}
            />
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 86,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-hub-actions-cell",
        renderCell: ({ row }) => (
          <ObjectAnalysisRowActions assessmentId={row.assessmentId} />
        ),
      },
    ],
    [navigate],
  );

  const isEmpty = rows.length === 0;

  return (
    <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={48}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        disableColumnFilter
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        showColumnVerticalBorder={false}
        pinnedColumnsSectionSeparator="border"
        slots={{
          toolbar: toolbarSlot,
          noRowsOverlay: NoRowsOverlay,
          columnsManagement: columnsManagementSlot,
        }}
        initialState={{ pinnedColumns: { right: ["actions"] } }}
        sx={
          [
            getRegulationsListDataGridSx(theme.palette.primary.main),
            aiHubListDataGridRowBorderSx,
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              minHeight: 400,
              "& .MuiDataGrid-toolbarQuickFilter": { display: "none" },
              "& .ai-hub-actions-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",
              },
            },
          ] as SxProps<Theme>
        }
      />
    </Box>
  );
}
